import { DAY_PART_SELECTIONS } from 'constants/menu';

import { format, getMinutes } from 'date-fns';

import { t } from 'state/intl/translations';
import { LANGUAGES } from 'state/intl/types';
import { transformDateWithTimeString } from 'state/menu/main-menu/hooks/use-active-day-parts';
import { findDayPart } from 'utils/daypart';
import { Region } from 'utils/environment/types';

import { IFormatDate, IGetDayPartConfig, IGetDayPartHours, IGetFormatDateByLocale } from './types';

const DATE_FORMAT = 'hh:mm a';
const DATE_FORMAT_FR_CA = `h 'h'`;
const DATE_FORMAT_FR_CA_MINUTES = `h 'h' mm`;

const getDateFormatByLocale = ({ region, language, hasMinutes }: IGetFormatDateByLocale) => {
  if (region === Region.CA && language === LANGUAGES.fr) {
    return hasMinutes ? DATE_FORMAT_FR_CA_MINUTES : DATE_FORMAT_FR_CA;
  }
  return DATE_FORMAT;
};

export const formatDate = ({ date, region, language }: IFormatDate) => {
  const dateToFormat = typeof date === 'string' ? transformDateWithTimeString(date) : date;
  const hasMinutes = getMinutes(dateToFormat) !== 0;
  const dateFormatByLocale = getDateFormatByLocale({ region, language, hasMinutes });
  return format(dateToFormat, dateFormatByLocale);
};

const getDayPartHours = ({ startTime, endTime, region, language }: IGetDayPartHours) => {
  if (!startTime || !endTime) {
    return null;
  }

  const daytimeStartTime = formatDate({ date: startTime, region, language });
  const daytimeEndTime = formatDate({ date: endTime, region, language });

  return t('daytimeMenuHeader', { daytimeStartTime, daytimeEndTime });
};

export const getDayPartConfig = ({
  activeDayPart,
  dayParts,
  region = Region.US,
  language = LANGUAGES.en,
}: IGetDayPartConfig): { title: string; dayPartHours: string | null } => {
  const mainMenuDayPart = findDayPart(DAY_PART_SELECTIONS.MAIN_MENU_KEY, dayParts);

  switch (activeDayPart.key) {
    case DAY_PART_SELECTIONS.BREAKFAST_KEY:
      return {
        title: t('breakfastMenu'),
        dayPartHours: getDayPartHours({
          startTime: activeDayPart.startTime,
          endTime: mainMenuDayPart?.startTime,
          region,
          language,
        }),
      };
    case DAY_PART_SELECTIONS.MAIN_MENU_KEY:
      return {
        title: t('daytimeMenu'),
        dayPartHours: t('daytimeMenuHeaderHours', {
          daytimeStartTime: formatDate({ date: activeDayPart.startTime, region, language }),
        }),
      };
    default:
      return { title: t('ourMenu'), dayPartHours: null };
  }
};
